import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { type RefObject, type SubmitEvent, useEffect, useMemo, useRef, useState } from 'react'

import type { CreateOrderRequest } from '@/bindings/CreateOrderRequest'
import type { DiningTable } from '@/bindings/DiningTable'
import type { Language } from '@/bindings/Language'
import type { OrderType } from '@/bindings/OrderType'
import type { OrderingItem } from '@/bindings/OrderingItem'
import type { QueueOrder } from '@/bindings/QueueOrder'
import type { StaffOrderingContext } from '@/bindings/StaffOrderingContext'
import { Button } from '@/components/button'
import { Checkbox, Field, Input, Select, Textarea } from '@/components/field'
import { FormError } from '@/components/form-error'
import { Card, Page } from '@/components/layout'
import { emptyIdentifierDraft, toLoginIdentifier } from '@/features/auth/identifier-draft'
import { IdentifierInput } from '@/features/auth/identifier-input'
import { allTablesQuery, backOfficeKeys, createOrder, staffOrderingQuery } from '@/features/back-office/api'
import { SESSION_PATH } from '@/features/back-office/sessions-page'
import { type Cart, EMPTY_CART, cartTotalMinor, prune, quantityOf, requestLines, setQuantity, useCart } from '@/features/ordering/cart'
import { CartLines, MAX_DELIVERY_ADDRESS_LEN, MAX_NOTE_LEN } from '@/features/ordering/cart-sheet'
import { MenuList } from '@/features/ordering/menu-list'
import { ApiError, claim } from '@/lib/api'
import { minorUnits } from '@/lib/currencies'
import { cx } from '@/lib/cx'
import { useLanguage } from '@/lib/i18n/store'
import { useManagedBranch } from '@/features/back-office/scope'
import { useT } from '@/lib/i18n/translate'
import { formatPrice } from '@/lib/menu-format'
import { fromMinor } from '@/lib/quantity'

const ORDER_PATH = '/$restaurantSlug/$branchSlug/manage/orders/$orderId'

/**
 * Staff create an order (PRD, G12): the branch menu with the customer page's controls, then
 * one form — take-away or a table, an optional customer account, the lines, note and address.
 * No customer gate applies (shop switch, mode, PIN, cap): the counter orders whenever staff
 * say so. Placing stays here for the next customer (2026-09-14): the page scrolls to the top,
 * a strip names the order and links to it — or to its session for dine-in — until the next
 * item is added. Every role in the branch.
 */
export function CreateOrderPage() {
  const t = useT()
  const language = useLanguage()
  const { restaurantSlug, branchSlug, restaurantId, branchId, context } = useManagedBranch()
  const ordering = useQuery(staffOrderingQuery(restaurantId, branchId))
  // Kept per branch in this tab, so a refresh mid-order loses nothing (G6's rule).
  const { cart, update, attempt, endAttempt } = useCart(`romi.staff-cart.${restaurantId}/${branchId}`)
  const form = useRef<HTMLElement>(null)
  const [placed, setPlaced] = useState<QueueOrder>()
  // After the commit, not before: the emptied cart shrinks the page, and Safari drops a scroll in flight.
  useEffect(() => {
    if (placed) window.scrollTo({ top: 0 })
  }, [placed])

  const items = useMemo(() => new Map(ordering.data?.items.map((item) => [item.id, item]) ?? []), [ordering.data])
  useEffect(() => {
    if (ordering.data) update((cart) => prune(cart, items))
  }, [ordering.data, items, update])

  const branch = context.branches.find((candidate) => candidate.id === branchId)
  const header = (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-xl font-semibold">{t('createOrder.title')}</h1>
      {branch && <span className="text-muted">{branch.name}</span>}
    </div>
  )
  if (ordering.isPending) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <p className="text-muted text-sm">{t('common.loading')}</p>
      </Page>
    )
  }
  if (!ordering.data) {
    return (
      <Page width="md" className="flex flex-col gap-4">
        {header}
        <FormError error={ordering.error} />
        <Button variant="secondary" onClick={() => void ordering.refetch()} className="self-start">
          {t('common.retry')}
        </Button>
      </Page>
    )
  }

  const data = ordering.data
  const total = fromMinor(cartTotalMinor(cart, items, data.branch.currency), data.branch.currency)
  return (
    <Page width="md" className="flex flex-col gap-4">
      {header}
      {placed && (
        <div role="status" className="border-success bg-surface flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3">
          <span className="font-medium">{t('createOrder.placed', { number: placed.number })}</span>
          {placed.session ? (
            <Link to={SESSION_PATH} params={{ restaurantSlug, branchSlug, sessionId: placed.session.id }} className="text-accent-ink font-medium hover:underline">
              {t('createOrder.viewSession', { name: placed.session.table_name })}
            </Link>
          ) : (
            <Link to={ORDER_PATH} params={{ restaurantSlug, branchSlug, orderId: placed.id }} className="text-accent-ink font-medium hover:underline">
              {t('createOrder.viewOrder')}
            </Link>
          )}
        </div>
      )}
      <MenuList
        categories={data.categories}
        items={data.items}
        view={{ main: context.settings.main_language, currency: data.branch.currency, readOnly: false }}
        quantityOf={(itemId) => quantityOf(cart, itemId)}
        onQuantity={(itemId, quantity) => {
          setPlaced(undefined)
          update((cart) => setQuantity(cart, itemId, quantity))
        }}
      />
      {cart.lines.length > 0 && (
        // Sticks above the phone bar until the form scrolls into view.
        <div className="bg-surface border-border sticky bottom-[calc(3.5rem+env(safe-area-inset-bottom))] z-20 -mx-4 flex items-center justify-between gap-3 border-t px-4 py-2 sm:-mx-6 sm:px-6 md:bottom-0">
          <span>
            <span className="font-medium">{t('order.itemCount', { count: cart.lines.length })}</span>{' '}
            <span className="text-muted">· {formatPrice(total, minorUnits(data.branch.currency), language)}</span>
          </span>
          <Button size="sm" onClick={() => form.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })}>
            {t('createOrder.review')}
          </Button>
        </div>
      )}
      <OrderForm
        ref={form}
        restaurantId={restaurantId}
        branchId={branchId}
        main={context.settings.main_language}
        context={data}
        items={items}
        cart={cart}
        update={update}
        attempt={attempt}
        endAttempt={endAttempt}
        onPlaced={setPlaced}
      />
    </Page>
  )
}

interface OrderFormProps {
  ref: RefObject<HTMLElement | null>
  restaurantId: string
  branchId: string
  /** The restaurant's main language: the T4.3 fallback for item names. */
  main: Language
  context: StaffOrderingContext
  items: ReadonlyMap<string, OrderingItem>
  cart: Cart
  update: (next: (cart: Cart) => Cart) => void
  /** T8.3: the idempotency key of this attempt, kept across retries of the same request. */
  attempt: () => string
  endAttempt: () => void
  onPlaced: (order: QueueOrder) => void
}

/** Where, for whom, what: the whole request, and the Place order button. */
function OrderForm({ ref, restaurantId, branchId, main, context, items, cart, update, attempt, endAttempt, onPlaced }: OrderFormProps) {
  const t = useT()
  const language = useLanguage()
  const queryClient = useQueryClient()
  const tables = useQuery(allTablesQuery(restaurantId, branchId))
  const [orderType, setOrderType] = useState<OrderType>('take_away')
  const [tableId, setTableId] = useState('')
  const [withCustomer, setWithCustomer] = useState(false)
  const [customer, setCustomer] = useState(() => emptyIdentifierDraft())
  const [note, setNote] = useState('')
  const [address, setAddress] = useState('')

  const { branch } = context
  const decimals = minorUnits(branch.currency)
  const price = (value: string) => formatPrice(value, decimals, language)
  const dineIn = orderType === 'dine_in'
  const table: DiningTable | undefined = dineIn ? tables.data?.find((candidate) => candidate.id === tableId) : undefined
  const showAddress = !dineIn && branch.delivery_address_enabled
  const unorderable = cart.lines.some((line) => !items.get(line.item_id)?.orderable)
  const total = fromMinor(cartTotalMinor(cart, items, branch.currency), branch.currency)

  const place = useMutation({
    mutationFn: () => {
      const body: CreateOrderRequest = {
        idempotency_key: attempt(),
        lines: requestLines(cart),
        ...(table ? { table_code: table.code } : {}),
        ...(withCustomer ? { customer: toLoginIdentifier(customer) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(showAddress && address.trim() ? { delivery_address: address.trim() } : {}),
      }
      return createOrder(restaurantId, branchId, body)
    },
    onSuccess: (order: QueueOrder) => {
      endAttempt()
      update(() => EMPTY_CART)
      // The next customer starts clean; the order type stays, a counter rarely switches it.
      setTableId('')
      setWithCustomer(false)
      setCustomer(emptyIdentifierDraft())
      setNote('')
      setAddress('')
      // The strip's link opens on this; the hint refetches the lists, the explicit calls cover a down stream.
      queryClient.setQueryData(backOfficeKeys.order(restaurantId, branchId, order.id), order)
      void queryClient.invalidateQueries({ queryKey: backOfficeKeys.queue(restaurantId, branchId) })
      void queryClient.invalidateQueries({ queryKey: backOfficeKeys.sessions(restaurantId, branchId) })
      onPlaced(order)
    },
    onError: (error) => {
      // A verdict (4xx) ends the attempt; a lost answer (network, 5xx) keeps its key for the retry.
      if (error instanceof ApiError && error.status < 500) endAttempt()
      // Sold-out lines: the menu tells the page what changed. A dead table: the picker too.
      void queryClient.invalidateQueries({ queryKey: backOfficeKeys.staffOrdering(restaurantId, branchId) })
      if (claim(error, 'TABLE_NOT_FOUND')) void queryClient.invalidateQueries({ queryKey: backOfficeKeys.tableAll(restaurantId, branchId) })
    },
  })
  const pending = place.isPending

  // The key names the whole request (T8.3): any change outside the cart starts a new attempt.
  function change<T>(set: (value: T) => void) {
    return (value: T) => {
      endAttempt()
      if (place.error) place.reset()
      set(value)
    }
  }
  const chooseType = change(setOrderType)
  const chooseTable = change(setTableId)
  const toggleCustomer = change(setWithCustomer)
  const editCustomer = change(setCustomer)
  const editNote = change(setNote)
  const editAddress = change(setAddress)

  function submit(event: SubmitEvent) {
    event.preventDefault()
    if (!canPlace) return
    place.mutate()
  }

  const lineError = claim(place.error, 'ITEM_UNAVAILABLE', 'INSUFFICIENT_STOCK', 'INVALID_QUANTITY', 'LINE_NOTE_TOO_LONG')
  const tableError = claim(place.error, 'TABLE_NOT_FOUND', 'SESSION_CLOSED')
  const customerError = claim(place.error, 'ACCOUNT_NOT_FOUND', 'INVALID_PHONE', 'INVALID_EMAIL')
  const bannerError = lineError || tableError || customerError ? undefined : place.error
  const noTables = tables.data !== undefined && tables.data.length === 0
  const canPlace = cart.lines.length > 0 && !unorderable && (!dineIn || table !== undefined)

  return (
    <Card ref={ref} aria-label={t('createOrder.form')} className="scroll-mt-14 flex flex-col gap-4">
      <h2 className="text-lg font-semibold">{t('createOrder.form')}</h2>
      <form onSubmit={submit} className="flex flex-col gap-4">
        <div role="group" aria-label={t('createOrder.type')} className="border-border grid grid-cols-2 gap-1 rounded-lg border p-1">
          {(['take_away', 'dine_in'] as const).map((type) => (
            <button
              key={type}
              type="button"
              aria-pressed={orderType === type}
              disabled={pending || (type === 'dine_in' && noTables)}
              onClick={() => chooseType(type)}
              className={cx(
                'min-h-9 rounded-md text-sm font-medium disabled:opacity-50',
                orderType === type ? 'bg-accent text-accent-fg' : 'text-fg hover:bg-accent-soft',
              )}
            >
              {t(type === 'take_away' ? 'order.takeAway' : 'createOrder.dineIn')}
            </button>
          ))}
        </div>
        {noTables && <p className="text-muted text-sm">{t('createOrder.noTables')}</p>}
        {dineIn && (
          <Field label={t('createOrder.table')} error={tableError}>
            <Select value={tableId} disabled={pending} onChange={(e) => chooseTable(e.target.value)}>
              <option value="">{t('createOrder.chooseTable')}</option>
              {tables.data?.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Checkbox label={t('createOrder.forCustomer')} checked={withCustomer} disabled={pending} onChange={(e) => toggleCustomer(e.target.checked)} />
        {withCustomer && (
          <div className="flex flex-col gap-3">
            <p className="text-muted text-sm">{t('createOrder.customerHint')}</p>
            <IdentifierInput value={customer} onChange={editCustomer} />
            <FormError error={customerError} />
          </div>
        )}

        <CartLines cart={cart} items={items} currency={branch.currency} main={main} disabled={pending} lineError={lineError} update={update} empty={t('createOrder.empty')} />

        <Field label={t('order.note')} hint={t('order.noteHint', { max: MAX_NOTE_LEN })}>
          <Textarea value={note} maxLength={MAX_NOTE_LEN} rows={2} disabled={pending} onChange={(e) => editNote(e.target.value)} />
        </Field>
        {showAddress && (
          <Field label={t('order.deliveryAddress')} hint={t('order.deliveryAddressHint', { max: MAX_DELIVERY_ADDRESS_LEN })}>
            <Input value={address} maxLength={MAX_DELIVERY_ADDRESS_LEN} autoComplete="off" disabled={pending} onChange={(e) => editAddress(e.target.value)} />
          </Field>
        )}

        <div className="border-border flex items-center justify-between border-t pt-3 text-lg">
          <span>{t('order.total')}</span>
          <span className="font-semibold tabular-nums">{price(total)}</span>
        </div>
        <FormError error={bannerError} />
        <Button type="submit" block loading={pending} disabled={!canPlace}>
          {t('createOrder.place')}
        </Button>
      </form>
    </Card>
  )
}
